'use client'
import { tw } from 'utils/classnames'
import Image from 'next/image'
import { ContentAssetType, ContentAssetVariant } from 'codegen/types'
import { useLocale, useTranslations } from 'next-intl'
import { defaultLanguage } from 'cf-i18n/constants'
import { useUserQuery } from 'auth/hooks'
import { rootPath } from '@repo/web-utils/root-path'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { LinkButton } from './button'
import { EmailIcon } from '@web/components/icons/email'
import { LFS_URL, NEXT_PUBLIC_CONTENT_IMAGES_ROOT_URL } from '@web/utils/env'
import { useProductDataQuery } from '@web/hooks/use-product-data'

interface ShareOptionsProps {
  className?: string
}

export function SocialShareOptions({ className }: ShareOptionsProps) {
  const { data: productContent } = useProductDataQuery()
  const locale = useLocale()
  const t = useTranslations('prod.product_description_page.share_earn')
  const { data: affiliateId } = useUserQuery(undefined, {
    select: data => data.me?.user?.affiliateInfo?.affiliateId,
  })
  const url = `${rootPath}${locale === defaultLanguage ? '' : `/${locale}`}${AppRoutes.PROD}${
    productContent?.slug ? productContent.slug : ''
  }-${productContent?.id}/ref/${affiliateId}/`
  const productImgPath =
    productContent?.publicAssets
      .filter(asset => asset.type === ContentAssetType.AssetTypePreview)
      .find(asset => asset.variant === ContentAssetVariant.AssetVariantDesktop)?.file_path ?? ''
  const productDescription = productContent?.description ?? ''

  return (
    <div className={tw('flex w-full flex-wrap gap-2', className)}>
      <LinkButton
        key="facebook"
        href={getFacebookLink(url)}
        icon={
          <Image
            src={`${LFS_URL}/web/pages/content/pdp/share/facebook.svg`}
            alt="facebook"
            width={24}
            height={24}
            unoptimized
          />
        }
        text={t('facebook')}
        className="border"
      />
      <LinkButton
        key="X(twitter)"
        href={getTwitterLink(url)}
        icon={
          <Image
            src={`${LFS_URL}/web/pages/content/pdp/share/x.svg`}
            alt="X(twitter)"
            width={24}
            height={24}
            unoptimized
          />
        }
        text={t('twitter')}
        className="border"
      />
      <LinkButton
        key="messenger"
        href={getFacebookMessengerLink(url, '166866887053973')}
        icon={
          <Image
            src={`${LFS_URL}/web/pages/content/pdp/share/fb_messenger.svg`}
            alt="messenger"
            width={24}
            height={24}
            unoptimized
          />
        }
        text={t('messenger')}
        className="border"
      />
      <LinkButton
        key="pinterest"
        href={getPinterestLink(url, productImgPath, productDescription)}
        icon={
          <Image
            src={`${LFS_URL}/web/pages/content/pdp/share/pinterest.svg`}
            alt="pinterest"
            width={24}
            height={24}
            unoptimized
          />
        }
        text={t('pinterest')}
        className="border"
      />
      <LinkButton
        key="reddit"
        href={getRedditLink(url)}
        icon={
          <Image
            src={`${LFS_URL}/web/pages/content/pdp/share/reddit.svg`}
            alt="reddit"
            width={24}
            height={24}
            unoptimized
          />
        }
        text={t('reddit')}
        className="border"
      />

      <LinkButton
        key="whatsapp"
        href={getWhatsAppLink(url)}
        icon={
          <Image
            src={`${LFS_URL}/web/pages/content/pdp/share/whatsapp.svg`}
            alt="whatsapp"
            width={20}
            height={22}
            unoptimized
          />
        }
        text={t('whatsapp')}
        className="border"
      />
      <LinkButton
        key="email"
        href={getEmailLink(url)}
        icon={<EmailIcon className="text-cf-violet" />}
        text={t('email')}
        className="border"
      />
    </div>
  )
}

export function getEmailLink(url: string) {
  const subject = encodeURIComponent('Hi! Check out this graphic on Creative Fabrica')
  const body = encodeURIComponent(
    `Hi! Check out this graphic on Creative Fabrica! What do you think: ${url} ?`,
  )
  return `mailto:?subject=${subject}&body=${body}`
}

export function getRedditLink(url: string) {
  const title = encodeURIComponent(
    'Hi! Check out this graphic on Creative Fabrica! What do you think?',
  )
  const text = encodeURIComponent(
    `Hi! Check out this graphic on Creative Fabrica, I love it! What do you think: ${url}`,
  )

  // @todo investigate why the text is not working
  return `https://www.reddit.com/submit?title=${title}&text=${text}`
}

export function getPinterestLink(url: string, productImgPath: string, productDescription: string) {
  return `https://www.pinterest.com/pin/create/button/?url='${url}&media=${`${NEXT_PUBLIC_CONTENT_IMAGES_ROOT_URL}/${productImgPath}`}&description='${productDescription}`
}

export function getWhatsAppLink(url: string) {
  const message = encodeURIComponent(
    `Hi! Check out this graphic on Creative Fabrica! What do you think: ${url} ?`,
  )
  return `https://api.whatsapp.com/send?text=${message}`
}

export function getFacebookMessengerLink(url: string, appId: string) {
  const link = encodeURIComponent(url)
  return `https://www.facebook.com/dialog/send?app_id=${appId}&link=${link}&redirect_uri=${link}`
}

export function getTwitterLink(url: string) {
  const text = encodeURIComponent(
    `Hi! Check out this graphic on Creative Fabrica! What do you think: ${url}`,
  )
  return `https://twitter.com/intent/tweet?text=${text}`
}

export function getFacebookLink(url: string) {
  return `https://www.facebook.com/sharer/sharer.php?u=${url}`
}

export function getTelegramLink(url: string) {
  const text = encodeURIComponent(
    `Hi! Check out this graphic on Creative Fabrica! What do you think: ${url}`,
  )

  return `https://t.me/share/url?url=${url}&text=${text}`
}
